import React, { useEffect, useState, useMemo, useCallback } from 'react';

import './App.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart, ReferenceLine, Label, Brush } from 'recharts';
import moment from 'moment';

// Constants
const MOBILE_BREAKPOINT = 768;
const COLORS = {
  primary: '#0056b3',
  textGray: '#666',
  textDark: '#333',
  borderLight: '#ddd',
  borderLighter: '#eee',
  hoverBg: '#f5f5f5',
  cardBg: '#f9f9f9',
};
const MAX_CONTENT_WIDTH = '1280px';

// Shared styles
const DESCRIPTION_STYLE = {
  textAlign: 'left',
  fontSize: '0.85rem',
  maxWidth: '800px',
  margin: '0 auto 20px'
};

const LINK_STYLE = {
  color: COLORS.primary,
  textDecoration: 'underline'
};


const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'rgba(40, 40, 40, 0.95)',
        border: '1px solid #666',
        borderRadius: '3px',
        padding: '4px 6px',
        fontSize: '11px',
        maxWidth: '200px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        color: 'white'
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '2px', color: 'white' }}>
          {payload[0].name}
        </div>
        <div style={{ color: '#ccc' }}>
          {moment.unix(parseFloat(label)).format('MM-DD HH:mm')}
        </div>
        <div style={{ color: payload[0].stroke, fontWeight: 'bold' }}>
          {parseFloat(payload[0].value).toFixed(3)}s
        </div>
      </div>
    );
  }

  return null;
};

function startAt(data, start) {
  let result = [];
  for (const item of data) {
    if (item.date >= start) {
      result.push(item);
    }
  }
  return result;
}

// Calculate statistics for each series
function calculateStats(data, allSeries, visibleSeries) {
  const stats = {};

  allSeries.forEach(series => {
    if (visibleSeries[series.key]) {
      const values = data
        .map(item => item[series.key])
        .filter(value => value !== undefined && value !== null && !isNaN(value))
        .map(value => parseFloat(value));

      if (values.length > 0) {
        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
        stats[series.key] = {
          mean: mean,
          count: values.length,
          color: series.color
        };
      }
    }
  });

  return stats;
}

// Component to display statistics
function StatsDisplay({ stats }) {
  if (!stats || Object.keys(stats).length === 0) {
    return (
      <div style={{
        marginTop: '2rem',
        textAlign: 'center'
      }}>
        <p>No data available for the selected range</p>
      </div>
    );
  }

  const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

  return (
    <div style={{
      width: '100%',
      maxWidth: MAX_CONTENT_WIDTH,
      margin: '0 auto',
      padding: isMobile ? '0 0.5rem' : '0'
    }}>
      {isMobile ? (
        // Mobile: Card layout
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {Object.entries(stats).map(([seriesName, stat]) => (
            <div
              key={seriesName}
              style={{
                padding: '1rem',
                borderLeft: `4px solid ${stat.color}`,
                backgroundColor: COLORS.cardBg,
                borderRadius: '4px'
              }}
            >
              <div style={{
                fontWeight: 'bold',
                color: stat.color,
                fontSize: '1rem',
                marginBottom: '0.5rem'
              }}>
                {seriesDisplayName[seriesName]}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.9rem',
                color: COLORS.textGray
              }}>
                <span><strong>Mean:</strong> {stat.mean.toFixed(3)}s</span>
                <span><strong>Data Points:</strong> {stat.count}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Desktop: Table layout
        <table style={{
          width: '100%',
          borderCollapse: 'collapse'
        }}>
          <tbody>
            {Object.entries(stats).map(([seriesName, stat]) => (
              <tr key={seriesName} style={{ borderBottom: `1px solid ${COLORS.borderLighter}` }}>
                <td style={{
                  padding: '0.75rem',
                  fontWeight: 'bold',
                  color: stat.color,
                  width: '40%',
                  fontSize: '1.2rem'
                }}>
                  {seriesDisplayName[seriesName]}
                </td>
                <td style={{
                  padding: '0.75rem',
                  fontSize: '1.2rem'
                }}>
                  <strong>Mean Cold Start:</strong> {stat.mean.toFixed(3)}s
                </td>
                <td style={{
                  padding: '0.75rem',
                  fontSize: '1.2rem'
                }}>
                  Data Points: {stat.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const seriesDisplayName = {
  "Cloudflare Workers": "Cloudflare Workers (Legacy)",
  "Cloudflare Workers 0.28.0": "Cloudflare Workers",
  "Cloudflare Rust Workers": "Cloudflare Rust Workers",
  "AWS Lambda": "AWS Lambda (No SnapStart)",
  "AWS Lambda HTTP Pkgs": "AWS Lambda (No SnapStart)",
  "AWS Lambda HTTP Pkgs Snapstart": "AWS Lambda (SnapStart)",
  "Cloudflare Workers HTTP Pkgs": "Cloudflare Workers (Legacy)",
  "Cloudflare Workers Vendored HTTP Pkgs": "Cloudflare Workers",
  "Cloud Run HTTP Pkgs": "Google Cloud Run"
};

// Show Code Section Component
function ShowCodeSection({ children }) {
  const [showCode, setShowCode] = useState(false);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <div style={{ maxWidth: '800px', width: '100%', margin: '0 0 20px 0', textAlign: 'left' }}>
        <button
          onClick={() => setShowCode(!showCode)}
          style={{
            padding: '4px 0',
            fontSize: '0.85rem',
            fontWeight: '500',
            backgroundColor: 'transparent',
            color: COLORS.textGray,
            border: 'none',
            cursor: 'pointer',
            marginBottom: showCode ? '15px' : '0',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = COLORS.textDark;
          }}
          onMouseLeave={(e) => {
            e.target.style.color = COLORS.textGray;
          }}
        >
          <span style={{ fontSize: '0.75rem' }}>{showCode ? '▼' : '▶'}</span>
          {showCode ? 'Hide Code' : 'Show Code'}
        </button>

        {showCode && <div>{children}</div>}
      </div>
    </div>
  );
}

function Chart({ data, isHttpPkgs, onDataChange }) {
  // Define all possible series with their colors
  const allSeries = isHttpPkgs ? [
    { key: "AWS Lambda HTTP Pkgs", color: "#146eb4" },
    { key: "AWS Lambda HTTP Pkgs Snapstart", color: "#06518c" },
    { key: "Cloud Run HTTP Pkgs", color: "#ea4335" },
    // { key: "Cloudflare Workers HTTP Pkgs", color: "#9422f2" },
    { key: "Cloudflare Workers Vendored HTTP Pkgs", color: "#f29422" },
  ] : [
    // { key: "Cloudflare Workers", color: "#9422f2" },
    { key: "Cloudflare Workers 0.28.0", color: "#f29422" },
    // { key: "Cloudflare Rust Workers", color: "#d15106" },
    { key: "AWS Lambda", color: "#146eb4" }
  ];

  // Initialize visibility state - all series visible by default
  const [visibleSeries, setVisibleSeries] = useState(
    allSeries.reduce((acc, series) => ({ ...acc, [series.key]: true }), {})
  );

  // Date range state
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [zoomDomain, setZoomDomain] = useState(null);
  const [timeRangeType, setTimeRangeType] = useState('default'); // 'hour', 'day', 'default'
  const [selectedTimeRange, setSelectedTimeRange] = useState('all'); // 'all', '7d', '4d', '1d', '6h'

  // Toggle series visibility
  const toggleSeries = (seriesKey) => {
    setVisibleSeries(prev => ({
      ...prev,
      [seriesKey]: !prev[seriesKey]
    }));
  };

  // Get min and max dates from data
  const getDateBounds = () => {
    if (!data || data.length === 0) return { min: null, max: null };
    const dates = data.map(item => parseInt(item.date));
    return {
      min: Math.min(...dates),
      max: Math.max(...dates)
    };
  };

  // Filter data by date range and visible series
  const getFilteredData = () => {
    let filteredData = data;

    // Filter by date range if set
    if (dateRange.start || dateRange.end) {
      filteredData = data.filter(item => {
        const itemDate = parseInt(item.date);
        let start, end;

        // Handle timestamp-based filtering (for hours)
        if (typeof dateRange.start === 'number') {
          start = dateRange.start;
          end = dateRange.end;
        } else {
          // Handle date-based filtering (for days)
          start = dateRange.start ? new Date(dateRange.start + 'T00:00:00').getTime() / 1000 : -Infinity;
          end = dateRange.end ? new Date(dateRange.end + 'T23:59:59').getTime() / 1000 : Infinity;
        }

        return itemDate >= start && itemDate <= end;
      });
    }

    // Filter by visible series
    return filteredData.map(item => {
      const filteredItem = { date: parseInt(item.date) };
      allSeries.forEach(series => {
        if (visibleSeries[series.key] && item[series.key] !== undefined) {
          filteredItem[series.key] = item[series.key];
        }
      });
      return filteredItem;
    });
  };

  // Handle preset date ranges
  const handlePresetRange = (days, hours = 0, rangeKey) => {
    const now = new Date();
    const totalMilliseconds = (days * 24 * 60 * 60 * 1000) + (hours * 60 * 60 * 1000);
    const startDate = new Date(now.getTime() - totalMilliseconds);

    setSelectedTimeRange(rangeKey);

    // Set time range type for X-axis formatting
    if (hours > 0 && days === 0) {
      setTimeRangeType('hour');
      setDateRange({
        start: Math.floor(startDate.getTime() / 1000),
        end: Math.floor(now.getTime() / 1000)
      });
    } else if (days === 1) {
      setTimeRangeType('day');
      setDateRange({
        start: startDate.toISOString().split('T')[0],
        end: now.toISOString().split('T')[0]
      });
    } else {
      setTimeRangeType('default');
      setDateRange({
        start: startDate.toISOString().split('T')[0],
        end: now.toISOString().split('T')[0]
      });
    }
    setZoomDomain(null);
  };

  // Reset to show all data
  const resetDateRange = () => {
    setDateRange({ start: null, end: null });
    setZoomDomain(null);
    setTimeRangeType('default');
    setSelectedTimeRange('all');
  };

  // Generate lines only for visible series
  const lines = allSeries.map(series =>
    visibleSeries[series.key] ? (
      <Line
        key={series.key}
        type="monotone"
        dataKey={series.key}
        stroke={series.color}
        dot={false}
        strokeWidth={2}
        onClick={() => toggleSeries(series.key)}
        style={{ cursor: 'pointer' }}
      />
    ) : null
  ).filter(Boolean);

  // Custom legend with clickable items
  const CustomLegend = () => {
    return (
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        marginBottom: '20px',
        gap: window.innerWidth > MOBILE_BREAKPOINT ? '15px' : '10px',
        padding: '0 10px'
      }}>
        {allSeries.map(series => (
          <div
            key={series.key}
            onClick={() => toggleSeries(series.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              opacity: visibleSeries[series.key] ? 1 : 0.5,
              textDecoration: visibleSeries[series.key] ? 'none' : 'line-through'
            }}
          >
            <div
              style={{
                width: window.innerWidth > MOBILE_BREAKPOINT ? '12px' : '10px',
                height: window.innerWidth > MOBILE_BREAKPOINT ? '12px' : '10px',
                backgroundColor: series.color,
                marginRight: '8px',
                border: visibleSeries[series.key] ? 'none' : `2px solid ${COLORS.borderLight}`
              }}
            />
            <span style={{ fontSize: window.innerWidth > MOBILE_BREAKPOINT ? '14px' : '12px' }}>{seriesDisplayName[series.key]}</span>
          </div>
        ))}
      </div>
    );
  };

  // Date range controls
  const DateRangeControls = () => {
    const bounds = getDateBounds();
    const minDate = bounds.min ? new Date(bounds.min * 1000).toISOString().split('T')[0] : '';
    const maxDate = bounds.max ? new Date(bounds.max * 1000).toISOString().split('T')[0] : '';

    return (
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '6px',
        marginBottom: '20px',
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto 20px auto',
        paddingLeft: window.innerWidth > MOBILE_BREAKPOINT ? '140px' : '10px',
        paddingRight: '10px'
      }}>
        {/* Preset buttons */}
          <button
            onClick={resetDateRange}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '500',
              backgroundColor: selectedTimeRange === 'all' ? COLORS.primary : 'transparent',
              color: selectedTimeRange === 'all' ? 'white' : COLORS.textGray,
              border: selectedTimeRange === 'all' ? 'none' : `1px solid ${COLORS.borderLight}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (selectedTimeRange !== 'all') {
                e.target.style.backgroundColor = COLORS.hoverBg;
                e.target.style.color = COLORS.textDark;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTimeRange !== 'all') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.textGray;
              }
            }}
          >
            All Data
          </button>
          <button
            onClick={() => handlePresetRange(7, 0, '7d')}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '500',
              backgroundColor: selectedTimeRange === '7d' ? COLORS.primary : 'transparent',
              color: selectedTimeRange === '7d' ? 'white' : COLORS.textGray,
              border: selectedTimeRange === '7d' ? 'none' : `1px solid ${COLORS.borderLight}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (selectedTimeRange !== '7d') {
                e.target.style.backgroundColor = COLORS.hoverBg;
                e.target.style.color = COLORS.textDark;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTimeRange !== '7d') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.textGray;
              }
            }}
          >
            Last Week
          </button>
          <button
            onClick={() => handlePresetRange(4, 0, '4d')}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '500',
              backgroundColor: selectedTimeRange === '4d' ? COLORS.primary : 'transparent',
              color: selectedTimeRange === '4d' ? 'white' : COLORS.textGray,
              border: selectedTimeRange === '4d' ? 'none' : `1px solid ${COLORS.borderLight}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (selectedTimeRange !== '4d') {
                e.target.style.backgroundColor = COLORS.hoverBg;
                e.target.style.color = COLORS.textDark;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTimeRange !== '4d') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.textGray;
              }
            }}
          >
            Last 4 Days
          </button>
          <button
            onClick={() => handlePresetRange(1, 0, '1d')}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '500',
              backgroundColor: selectedTimeRange === '1d' ? COLORS.primary : 'transparent',
              color: selectedTimeRange === '1d' ? 'white' : COLORS.textGray,
              border: selectedTimeRange === '1d' ? 'none' : `1px solid ${COLORS.borderLight}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (selectedTimeRange !== '1d') {
                e.target.style.backgroundColor = COLORS.hoverBg;
                e.target.style.color = COLORS.textDark;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTimeRange !== '1d') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.textGray;
              }
            }}
          >
            Last 1 Day
          </button>
          <button
            onClick={() => handlePresetRange(0, 6, '6h')}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '500',
              backgroundColor: selectedTimeRange === '6h' ? COLORS.primary : 'transparent',
              color: selectedTimeRange === '6h' ? 'white' : COLORS.textGray,
              border: selectedTimeRange === '6h' ? 'none' : `1px solid ${COLORS.borderLight}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              if (selectedTimeRange !== '6h') {
                e.target.style.backgroundColor = COLORS.hoverBg;
                e.target.style.color = COLORS.textDark;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTimeRange !== '6h') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.textGray;
              }
            }}
          >
            Last 6 Hours
          </button>
      </div>
    );
  };

  // Memoize the filtered data to prevent unnecessary recalculations
  const chartData = useMemo(() => getFilteredData(), [
    data,
    dateRange.start,
    dateRange.end,
    visibleSeries
  ]);

  // Notify parent component of data changes for statistics
  useEffect(() => {
    if (onDataChange) {
      onDataChange(chartData, allSeries, visibleSeries);
    }
  }, [chartData]);

  // Dynamic tick formatter based on time range
  const getTickFormatter = () => {
    switch (timeRangeType) {
      case 'hour':
        return (unixTime) => moment.unix(parseFloat(unixTime)).format('HH:mm');
      case 'day':
        return (unixTime) => moment.unix(parseFloat(unixTime)).format('MM-DD HH:mm');
      default:
        return (unixTime) => moment.unix(parseFloat(unixTime)).format('YYYY-MM-DD');
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: MAX_CONTENT_WIDTH, margin: '0 auto' }}>
      <CustomLegend />
      <DateRangeControls />
      <ResponsiveContainer width="100%" height={window.innerWidth > MOBILE_BREAKPOINT ? 800 : 500}>
        <ComposedChart
          data={chartData}
          margin={{
            top: 5,
            right: window.innerWidth > MOBILE_BREAKPOINT ? 30 : 10,
            left: window.innerWidth > MOBILE_BREAKPOINT ? 80 : 40,
            bottom: window.innerWidth > MOBILE_BREAKPOINT ? 80 : 60,
          }}
        >
        {/* <CartesianGrid strokeDasharray="3 3" /> */}
        <XAxis
          dataKey="date"
          tickFormatter={getTickFormatter()}
          scale="time"
          style={{ fontSize: window.innerWidth > MOBILE_BREAKPOINT ? "1rem" : "0.5rem" }}
          type="number"
          domain={['dataMin', 'dataMax']}
          tick={{ dy: 10 }}
        >
          <Label
                style={{
                    textAnchor: "middle",
                    fontSize: window.innerWidth > MOBILE_BREAKPOINT ? "1.5rem" : "1rem",
                    marginRight: "2pt"
                }}
              dy={window.innerWidth > MOBILE_BREAKPOINT ? 40 : 30}
              value={"Date"} />
        </XAxis>
        <YAxis style={{ fontSize: window.innerWidth > MOBILE_BREAKPOINT ? "1.5rem" : "0.75rem" }}>
          <Label
              style={{
                  textAnchor: "middle",
                  fontSize: window.innerWidth > MOBILE_BREAKPOINT ? "2rem" : "1rem",
                  marginRight: "2pt"
              }}
            dx={window.innerWidth > MOBILE_BREAKPOINT ? -80 : -40}
            angle={270}
            value={"Cold Start (seconds)"} />
        </YAxis>
        {lines}
        <Tooltip content={<CustomTooltip />} />
      </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}


function App() {

  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHttpLoaded, setIsHttpLoaded] = useState(false);

  const dummy = [{"date":"1718556080","Cloudflare Workers":"1.306999921798706"},{"date":"1720506126","AWS Lambda":"0.13899998664855957"},{"date":"1720506120","Cloudflare Workers":"0.6329999446868896"},{"date":"1720506320","Cloudflare Workers":"0.8329999446868896"},{"date":"1719770506","AWS Lambda":"0.379000186920166"},{"date":"1720506326","AWS Lambda":"0.33899998664855957"}];

  const [response, setResponse] = useState(null);
  const [httpPkgsResponse, setHttpPkgsResponse] = useState(null);
  const [mainChartStats, setMainChartStats] = useState({});
  const [httpChartStats, setHttpChartStats] = useState({});

  const getTabFromHash = () => {
    const hash = window.location.hash.slice(1);
    return hash === 'bare' ? 'hello-world' : hash === 'packages' ? 'http-packages' : 'http-packages';
  };

  const [activeTab, setActiveTab] = useState(getTabFromHash);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);


  // Callback functions to handle chart data changes
  const handleMainChartDataChange = useCallback((chartData, allSeries, visibleSeries) => {
    const stats = calculateStats(chartData, allSeries, visibleSeries);
    setMainChartStats(stats);
  }, []);

  const handleHttpChartDataChange = useCallback((chartData, allSeries, visibleSeries) => {
    const stats = calculateStats(chartData, allSeries, visibleSeries);
    setHttpChartStats(stats);
  }, []);

  function fetchData() {
    fetch("https://api.cold.picheta.me/list.json")
      .then(res => res.json())
      .then(
        (response) => {
          setIsLoaded(true);
          setResponse(response);
        },
        (error) => {
          setIsLoaded(true);
          setError(error);
        }
      )

    fetch("https://api.cold.picheta.me/list_http_pkgs.json")
      .then(res => res.json())
      .then(
        (response) => {
          setIsHttpLoaded(true);
          setHttpPkgsResponse(response);
        },
        (error) => {
          setError(error);
          setIsHttpLoaded(true);
        }
      )
  }

  let content = null;

  if (error) {
    content = (
      <>
        <h1 className="text-xl text-center">We had a problem</h1>
        <p className="text-sm text-center">The error we experienced is '{error.message}'. This may be a temporary problem..</p>
      </>
    );
  } else if (!isLoaded || !isHttpLoaded) {
    content = (<p>Loading...</p>);
  } else if (response == null || httpPkgsResponse == null) {
    content = (
      <>
        <h1 className="text-xl text-center">Couldn't find results this time</h1>
        <p className="text-sm text-center">Try again later.</p>
      </>
    )
  } else {

    content = (<>
        <h2 style={{ textAlign: 'center', marginBottom: '30px', fontSize: '1.75rem' }}>Serverless Python Benchmarks</h2>

        <p style={DESCRIPTION_STYLE}>
          This benchmark measures the cold start performance of various configurations of{' '}
          <a href="https://developers.cloudflare.com/workers/languages/python/" target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>Cloudflare Python Workers</a>
          {' '}and compares them against other providers. The data is collected and maintained by Cloudflare. Scripts are regularly re-submitted with slightly different content to
          force cold starts. Then the cold start times are recorded using a cron job.
        </p>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '0',
          borderBottom: `2px solid ${COLORS.borderLight}`,
          maxWidth: MAX_CONTENT_WIDTH,
          margin: '0 auto'
        }}>
          <button
            onClick={() => window.location.hash = 'packages'}
            style={{
              padding: window.innerWidth > MOBILE_BREAKPOINT ? '12px 24px' : '10px 16px',
              fontSize: window.innerWidth > MOBILE_BREAKPOINT ? '14px' : '13px',
              fontWeight: '600',
              backgroundColor: activeTab === 'http-packages' ? 'white' : 'transparent',
              color: activeTab === 'http-packages' ? COLORS.primary : COLORS.textGray,
              border: 'none',
              borderBottom: activeTab === 'http-packages' ? `3px solid ${COLORS.primary}` : '3px solid transparent',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              marginBottom: '-2px',
              position: 'relative',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'http-packages') {
                e.target.style.backgroundColor = COLORS.hoverBg;
                e.target.style.color = COLORS.textDark;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'http-packages') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.textGray;
              }
            }}
          >
            Cold Start with Packages
          </button>
          <button
            onClick={() => window.location.hash = 'bare'}
            style={{
              padding: window.innerWidth > MOBILE_BREAKPOINT ? '12px 24px' : '10px 16px',
              fontSize: window.innerWidth > MOBILE_BREAKPOINT ? '14px' : '13px',
              fontWeight: '600',
              backgroundColor: activeTab === 'hello-world' ? 'white' : 'transparent',
              color: activeTab === 'hello-world' ? COLORS.primary : COLORS.textGray,
              border: 'none',
              borderBottom: activeTab === 'hello-world' ? `3px solid ${COLORS.primary}` : '3px solid transparent',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              marginBottom: '-2px',
              position: 'relative',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'hello-world') {
                e.target.style.backgroundColor = COLORS.hoverBg;
                e.target.style.color = COLORS.textDark;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'hello-world') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.textGray;
              }
            }}
          >
            Hello World
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'http-packages' && (
          <>
            <h4>Cold start: Python with Packages</h4>
            <p style={DESCRIPTION_STYLE}>
              This benchmark measures the cold start performance of a Python script that imports{' '}
              <a href="https://fastapi.tiangolo.com/" target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>FastAPI</a>,{' '}
              <a href="https://www.python-httpx.org/" target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>httpx</a>, and{' '}
              <a href="https://docs.pydantic.dev/" target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>pydantic</a>.
              On start, each package is loaded into the top-level scope.

              This is meant to simulate real-world startups of Python scripts where developers often rely on popular web frameworks and HTTP clients.
            </p>

            <div style={{
              ...DESCRIPTION_STYLE,
              backgroundColor: '#f0f8ff',
              padding: '15px',
              borderRadius: '6px',
              borderLeft: '4px solid #146eb4',
              marginTop: '20px'
            }}>
              <strong>Note:</strong> Python Workers support memory snapshots by default and for no additional cost. The benchmarks below show
              AWS Lambda both with and without{' '}
              <a href="https://docs.aws.amazon.com/lambda/latest/dg/snapstart.html" target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>SnapStart</a> enabled, which is similar to Python Worker's snapshots but isn't free and requires additional set up prior to use.
            </div>

            <ShowCodeSection>
              <h5 style={{ fontSize: '1rem', marginTop: '15px', marginBottom: '10px', fontWeight: '600' }}>AWS Lambda</h5>
              <pre style={{
                backgroundColor: COLORS.cardBg,
                padding: '15px',
                borderRadius: '4px',
                border: `1px solid ${COLORS.borderLight}`,
                overflow: 'auto',
                fontSize: '0.85rem',
                lineHeight: '1.5'
              }}>
                <code>{`import fastapi, pydantic, httpx

def lambda_handler(event, context):
    return {
        'statusCode': 200,
        'body': 'The key is: \${key}'
    }`}</code>
              </pre>

              <h5 style={{ fontSize: '1rem', marginTop: '20px', marginBottom: '10px', fontWeight: '600' }}>Google Cloud Run</h5>
              <pre style={{
                backgroundColor: COLORS.cardBg,
                padding: '15px',
                borderRadius: '4px',
                border: `1px solid ${COLORS.borderLight}`,
                overflow: 'auto',
                fontSize: '0.85rem',
                lineHeight: '1.5'
              }}>
                <code>{`import functions_framework
import fastapi, pydantic, httpx

@functions_framework.http
def hello_http(request):
    return "The key is: \${key}"`}</code>
              </pre>

              <h5 style={{ fontSize: '1rem', marginTop: '20px', marginBottom: '10px', fontWeight: '600' }}>Cloudflare Workers</h5>
              <pre style={{
                backgroundColor: COLORS.cardBg,
                padding: '15px',
                borderRadius: '4px',
                border: `1px solid ${COLORS.borderLight}`,
                overflow: 'auto',
                fontSize: '0.85rem',
                lineHeight: '1.5'
              }}>
                <code>{`from workers import Response, WorkerEntrypoint
import fastapi, pydantic, httpx

class Default(WorkerEntrypoint):
    async def fetch(self, request, env):
        return Response("The key is: \${key}")`}</code>
              </pre>
            </ShowCodeSection>

            <Chart data={httpPkgsResponse} isHttpPkgs={true} onDataChange={handleHttpChartDataChange} />
            <StatsDisplay stats={httpChartStats} />
          </>
        )}

        {activeTab === 'hello-world' && (
          <>
            <h4>Cold start: Hello World</h4>
            <p style={DESCRIPTION_STYLE}>
              This benchmark measures the cold start performance of a Python script that does nothing on startup.

              This is useful for measuring the time it takes to cold start only the Python runtime, but may underestimate
              real-world cold start times where additional packages and frameworks are loaded.

              See the <a href="/#packages" rel="noopener noreferrer" style={LINK_STYLE}>packages benchmarks</a> as well.
            </p>

            <div style={{
              ...DESCRIPTION_STYLE,
              backgroundColor: '#f0f8ff',
              padding: '15px',
              borderRadius: '6px',
              borderLeft: '4px solid #146eb4',
              marginTop: '20px'
            }}>
              <strong>Note:</strong> The AWS Lambda benchmarks currently use standard Lambdas without{' '}
              <a href="https://docs.aws.amazon.com/lambda/latest/dg/snapstart.html" target="_blank" rel="noopener noreferrer" style={LINK_STYLE}>SnapStart</a>.
              We will be adding Lambda SnapStart benchmarks shortly.
            </div>

            <ShowCodeSection>
              <h5 style={{ fontSize: '1rem', marginTop: '15px', marginBottom: '10px', fontWeight: '600' }}>AWS Lambda</h5>
              <pre style={{
                backgroundColor: COLORS.cardBg,
                padding: '15px',
                borderRadius: '4px',
                border: `1px solid ${COLORS.borderLight}`,
                overflow: 'auto',
                fontSize: '0.85rem',
                lineHeight: '1.5'
              }}>
                <code>{`
def lambda_handler(event, context):
    return {
        'statusCode': 200,
        'body': 'The key is: \${key}'
    }`}</code>
              </pre>

              <h5 style={{ fontSize: '1rem', marginTop: '20px', marginBottom: '10px', fontWeight: '600' }}>Cloudflare Workers</h5>
              <pre style={{
                backgroundColor: COLORS.cardBg,
                padding: '15px',
                borderRadius: '4px',
                border: `1px solid ${COLORS.borderLight}`,
                overflow: 'auto',
                fontSize: '0.85rem',
                lineHeight: '1.5'
              }}>
                <code>{`from workers import Response, WorkerEntrypoint

class Default(WorkerEntrypoint):
    async def fetch(self, request, env):
        return Response("The key is: \${key}")`}</code>
              </pre>
            </ShowCodeSection>

            <Chart data={response} isHttpPkgs={false} onDataChange={handleMainChartDataChange} />
            <StatsDisplay stats={mainChartStats} />
          </>
        )}
      </>
    );
  }


  return (
    <div className="App">
      <header className="App-header">
        {content}
      </header>
    </div>
  );
}

export default App;
